import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Document, typeDocument } from 'src/app/core/models/document.model';
import { GedService, MetaTeleversement } from 'src/app/core/services/entities/ged.service';
import { FermeStateService } from 'src/app/core/services/entities/ferme-state.service';
import { ConfirmService } from './confirm.service';
import { ToastService } from './toast.service';

/**
 * Bloc « Documents » d'un objet métier (facture, transaction, contact, lot…) :
 * liste des documents reliés avec vignette, ouverture, ajout pré-relié et mise à la corbeille.
 */
@Component({
  selector: 'bay-documents-lies',
  template: `
    <section class="bay-doclies" [attr.aria-label]="titre">
      <div class="d-flex align-items-center justify-content-between mb-2">
        <h3 class="bay-section-h">{{ titre }} <span *ngIf="documents.length" class="bay-count">{{ documents.length }}</span></h3>
      </div>
      <p *ngIf="chargement" class="bay-hint">Chargement…</p>
      <ul *ngIf="documents.length" class="bay-doclies-list">
        <li *ngFor="let d of documents" class="bay-doclies-item">
          <button type="button" class="bay-doclies-open" (click)="ged.ouvrir(d)" [attr.aria-label]="'Ouvrir ' + d.nom">
            <img *ngIf="d.miniatureDisponible" [bayAuthSrc]="miniature(d)" alt="" class="bay-doclies-thumb">
            <span *ngIf="!d.miniatureDisponible" class="material-symbols-outlined bay-doclies-icon"
              [style.color]="type(d).color" aria-hidden="true">{{ type(d).icon }}</span>
            <span class="min-w-0 text-start">
              <span class="bay-doclies-nom">{{ d.nom }}</span>
              <span class="bay-doclies-meta">{{ type(d).label }} · {{ d.createdAt | dateFr:'court' }}<ng-container *ngIf="d.tailleFichier"> · {{ d.tailleFichier | taille }}</ng-container></span>
            </span>
          </button>
          <button *ngIf="!lectureSeule && d.origine !== 'GENERE'" type="button" class="bay-icon-btn"
            (click)="supprimer(d)" [attr.aria-label]="'Mettre ' + d.nom + ' à la corbeille'">
            <span class="material-symbols-outlined" aria-hidden="true">delete</span>
          </button>
        </li>
      </ul>
      <p *ngIf="!chargement && !documents.length && lectureSeule" class="bay-hint">Aucun document.</p>
      <bay-document-uploader *ngIf="!lectureSeule" [meta]="metaAjout" [compact]="true"
        [aide]="documents.length ? '' : aide" (televerse)="ajoute($event)"></bay-document-uploader>
    </section>
  `
})
export class DocumentsLiesComponent implements OnChanges {
  @Input() lienType = '';
  @Input() lienId?: number;
  @Input() lienLabel = '';
  @Input() typeDefaut = '';
  @Input() dossierCode = '';
  @Input() titre = 'Documents';
  @Input() aide = 'Reçus, bons, contrats, photos : ajoutez-les ici, ils sont aussi classés dans Documents.';
  @Input() lectureSeule = false;

  documents: Document[] = [];
  chargement = false;

  constructor(public ged: GedService, private fermeState: FermeStateService,
              private confirm: ConfirmService, private toast: ToastService) {}

  get metaAjout(): MetaTeleversement {
    return { lienType: this.lienType, lienId: this.lienId, lienLabel: this.lienLabel,
      type: this.typeDefaut || undefined, dossierCode: this.dossierCode || undefined };
  }

  ngOnChanges(c: SimpleChanges): void {
    if ((c.lienType || c.lienId) && this.lienType && this.lienId) this.charger();
  }

  charger(): void {
    this.chargement = true;
    this.ged.getDocumentsLies(this.fermeState.fermeId, this.lienType, this.lienId!).subscribe({
      next: d => { this.documents = d; this.chargement = false; },
      error: () => { this.chargement = false; }
    });
  }

  type(d: Document) { return typeDocument(d.type); }
  miniature(d: Document): string { return `${environment.BASE_URL}/api/documents/${d.id}/miniature`; }

  ajoute(d: Document): void {
    this.documents = [d, ...this.documents];
  }

  async supprimer(d: Document): Promise<void> {
    if (!(await this.confirm.danger('Mettre ce document à la corbeille ?', `${d.nom} — restaurable pendant 30 jours depuis Documents.`, 'Mettre à la corbeille'))) return;
    this.ged.deleteDocument(d.id!).subscribe({
      next: () => {
        this.documents = this.documents.filter(x => x.id !== d.id);
        this.toast.succes('Document mis à la corbeille', { actions: [{ label: 'Annuler', action: () => this.restaurer(d) }] });
      },
      error: err => this.toast.erreurHttp(err, 'Suppression impossible.')
    });
  }

  private restaurer(d: Document): void {
    this.ged.restaurer(d.id!).subscribe(r => this.documents = [r, ...this.documents]);
  }
}
